import { Category } from 'components/product/category/Category'
import { useLocation, useSearchParams } from 'react-router-dom'
import {
    constructOptionsSelect,
    constructSelectedValue,
    convertErrorMessage,
    downloadFile,
    getAllQueryParams,
    getObjectName,
    setQueryParams,
    uploadFileToS3,
} from 'utils/utils'
import { INITIAL_DATA_FILTER } from 'configs/product/configTableCategory'
import { useEffect, useState } from 'react'
import { ACTION_TYPE } from 'constants/constant'
import { useFetch } from 'services/useFetch'
import { API_CATEGORY } from 'services/apiCategory'
import { Alert } from 'avo-ui'
import moment from 'moment'
import { useValidation } from 'hooks/useValidation'
import { CATEGORY_VALIDATION } from 'services/validation/categoryValidation'

const INIT_STATE = {
    image_url: null,
    parent: null,
    name: null,
    slug: null,
}

const FIELD_NAME_DETAIL = {
    image_url: 'Thumbnail',
    parent_id: 'Parent category',
    name: 'Nama kategori',
    slug: 'Category slug',
}

export const CategoryContainer = () => {
    const location = useLocation()
    const [searchParams, setSearchParams] = useSearchParams()
    const query = searchParams.size > 0 ? getAllQueryParams(searchParams) : INITIAL_DATA_FILTER
    const [selectedIds, setSelectedIds] = useState([])
    const [categoryDetail, setCategoryDetail] = useState({})
    const [parentOptions, setParentOptions] = useState([])
    const [isResetSelectedItem, setIsResetSelectedItem] = useState(false)
    const { fetching: getCategoryList, data: categoryList, isLoading } = useFetch(API_CATEGORY.LIST)
    const { fetching: deleteCategory, isLoading: isDeleting } = useFetch(API_CATEGORY.DELETE)
    const { fetching: exportCategory, isLoading: isExporting } = useFetch(API_CATEGORY.EXPORT)
    const { fetching: getParentList } = useFetch(API_CATEGORY.PARENT_LIST)
    const { fetching: getUrlThumbnail } = useFetch(API_CATEGORY.URL_THUMBNAIL)
    const { fetching: createCategory, isLoading: isCreating } = useFetch(API_CATEGORY.CREATE)
    const { fetching: updateCategory, isLoading: isUpdating } = useFetch(API_CATEGORY.UPDATE)
    const [errorMessage, setErrorMessage] = useState({})
    const { validateForm, errorForm, clearErrorForm } = useValidation({
        form: categoryDetail,
        schema: CATEGORY_VALIDATION,
    })
    const [modal, setModal] = useState({
        export: false,
        delete: false,
        form: false,
    })

    useEffect(() => {
        fetchCategoryList()
    }, [location?.search])

    const onChangeQuery = (queryParams) => {
        const isReset = queryParams.hasOwnProperty('search')
        if (isReset) setIsResetSelectedItem(!isResetSelectedItem)
        const params = setQueryParams({ ...query, ...queryParams })
        setSearchParams(params, { replace: true })
    }

    const fetchCategoryList = (item = { ...query }) => {
        getCategoryList(item)
    }

    const setShowModal = (item) => {
        setModal({ ...modal, ...item })
    }

    const setShowAlert = (message, itemModal) => {
        setShowModal(itemModal)
        return Alert({
            icon: 'success',
            title: 'Success',
            message,
        })
    }

    const onExport = () => {
        const { page, limit, ...queryRest } = query
        const payload = { ...queryRest, ids: selectedIds }
        exportCategory(payload, '', {}, 'blob').then((response) => {
            downloadFile(null, response, `Category-Export-${moment().format('DD-MM-YYYY')}.xlsx`)
            setShowAlert('Category Exported !', { export: false })
        })
    }

    const onSelectItemHandler = (item) => {
        const ids = item?.map((i) => i?.id)
        setSelectedIds(ids)
    }

    const onActionRow = ({ item, actionType }) => {
        switch (actionType) {
            case ACTION_TYPE.CREATE:
                setCategoryDetail(INIT_STATE)
                setShowModal({ form: true })
                break
            case ACTION_TYPE.EDIT:
                const constructItem = { ...item, parent: constructSelectedValue(item?.parent, 'name', 'id') }
                const { product_count, ...newItem } = constructItem
                setCategoryDetail(newItem)
                setShowModal({ form: true })
                break
            default:
                setShowModal({ delete: true })
        }
    }

    const onHideForm = () => {
        setShowModal({ form: false })
        setErrorMessage({})
        clearErrorForm()
    }

    const onSearchParent = (value = '') => {
        getParentList({ search: value }).then((response) => {
            const options = constructOptionsSelect(response?.data, 'name', 'id')
            setParentOptions(options)
        })
    }

    const onChangeForm = (e) => {
        const { name, value } = e
        setCategoryDetail({ ...categoryDetail, [name]: value })
    }

    const onDelete = () => {
        deleteCategory({ ids: selectedIds }).then(() => {
            setIsResetSelectedItem(!isResetSelectedItem)
            setShowAlert('Category Deleted !', { delete: false })
            if (categoryList?.data?.results?.length === 1 && Number(query?.page) !== 1) {
                onChangeQuery({ page: 1 })
            } else {
                fetchCategoryList()
            }
        })
    }

    const setUploadThumbnail = async () => {
        try {
            const s3Urls = await getUrlThumbnail({ file: categoryDetail?.image_url?.name?.replace(/\s/g, '') })
            await uploadFileToS3(s3Urls?.data, categoryDetail?.image_url?.file)
            onSave(`${s3Urls.data.url}${s3Urls.data.fields.key}`)
        } catch (err) {
            console.log('upload failed')
        }
    }

    const onSubmit = () => {
        setErrorMessage({})
        validateForm().then(() => {
            if (!categoryDetail?.image_url?.name) return onSave()
            setUploadThumbnail()
        })
    }

    const onSave = (url = categoryDetail?.image_url) => {
        const constructCategoryDetail = {
            ...categoryDetail,
            image_url: url,
            parent_id: categoryDetail?.parent?.value || null,
        }
        const { id, parent, ...payload } = constructCategoryDetail
        if (!id) {
            createCategory(payload)
                .then(() => {
                    setShowAlert('Category Created !', { form: false })
                    fetchCategoryList()
                })
                .catch((error) => setErrorMessage(convertErrorMessage(error?.data, FIELD_NAME_DETAIL)))
        } else {
            updateCategory(payload, id)
                .then(() => {
                    setShowAlert('Category Updated !', { form: false })
                    fetchCategoryList()
                })
                .catch((error) => setErrorMessage(convertErrorMessage(error?.data, FIELD_NAME_DETAIL)))
        }
    }

    useEffect(() => {
        const isShowCreate = !categoryDetail?.id && !categoryDetail?.parent
        const isShowUpdate = categoryDetail?.id && categoryDetail?.parent
        if (modal?.form && (isShowCreate || isShowUpdate)) onSearchParent()
    }, [modal?.form])

    const props = {
        dataList: categoryList?.data,
        categoryDetail,
        modal,
        setShowModal,
        query,
        onChangeQuery,
        selectedIds,
        onSubmit,
        onDelete,
        onExport,
        onSelectItemHandler,
        onActionRow,
        onHideForm,
        onChangeForm,
        onSearchParent,
        parentOptions,
        errorForm: { ...errorForm, ...errorMessage },
        isLoading,
        isDeleting,
        isExporting,
        isCreating,
        isUpdating,
        isResetSelectedItem,
    }

    return <Category {...props} />
}
